import { unwrapResult } from '@reduxjs/toolkit';
import { useCallback } from 'react';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';
import { useSnackBar } from '../use-snackbar';
import { useFormik } from 'formik';
import {
  EmailerDetails,
  UpdateEmailerDetailsRequest,
  UploadImageEmailAssetPayload
} from 'src/redux/slices/emailer';
import { getDirtyValues } from 'src/utils/formikUtils';
import { usePermissions } from '../use-permissions';
import { useNavigate } from 'react-router';

const { actions, selectors } = slices.emailer;

interface Params {
  emailerId?: number;
  isCreateForm?: boolean;
}

export const useEmailer = (params?: Params) => {
  const dispatch = useAppDispatch();
  const snackbar = useSnackBar();
  const navigate = useNavigate();
  const { canCreateEmailer, canUpdateEmailer } = usePermissions();

  // selectors
  const emailerList = useAppSelector(selectors.selectEmailerList);
  const meta = useAppSelector(selectors.selectMeta);
  const isLoading = useAppSelector(selectors.selectIsLoading);
  const filters = useAppSelector(selectors.selectFilters);
  const emailerDetails = useAppSelector(selectors.selectDetails);

  // functions
  const createEmailer = useCallback(
    async (payload) => {
      if (!canCreateEmailer) {
        snackbar.show({
          severity: 'error',
          message: 'User does not have permission to create an emailer'
        });
        return;
      }

      const res = unwrapResult(
        await dispatch(actions.createEmailerThunk(payload))
      );

      const { success, message, errors, data } = res.originalData;

      if (success) {
        snackbar.show({
          severity: 'success',
          message: message ?? 'Created Emailer Successfully.'
        });
        navigate(`/app/emailer/${data?.id}`);
      } else {
        snackbar.show({
          severity: 'error',
          message: errors
            ? Object.values(errors).join(', ')
            : message ??
              'Failed to create emailer. Please try again later or contact support'
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const updateEmailerDetails = useCallback(
    async (payload: UpdateEmailerDetailsRequest) => {
      const res = unwrapResult(
        await dispatch(
          actions.updateEmailerDetailsThunk({
            id: params?.emailerId,
            ...payload
          })
        )
      );

      const { success, message, errors } = res.originalData;

      if (success) {
        snackbar.show({
          severity: 'success',
          message: message ?? 'Updated Emailer Successfully.'
        });

        getEmailerDetails();
      } else {
        snackbar.show({
          severity: 'error',
          message: errors
            ? Object.values(errors).join(', ')
            : message ??
              'Failed to update emailer. Please try again later or contact support'
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const getEmailerList = useCallback(
    async (params?: { asOptions: boolean }) => {
      const res = unwrapResult(
        await dispatch(
          actions.getEmailerListThunk(
            params?.asOptions ? { limit: 999 } : filters // get everything when called fetching list as options
          )
        )
      );

      const { success, message, errors } = res.originalData;

      if (!success) {
        snackbar.show({
          severity: 'error',
          message: errors
            ? Object.values(errors).join(', ')
            : message ??
              'Failed to get emailer list. Please try again later or contact support'
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters]
  );

  const getEmailerDetails = useCallback(
    async () => {
      if (!params?.emailerId) {
        return;
      }

      const res = unwrapResult(
        await dispatch(
          actions.getEmailerDetailsThunk({ id: params?.emailerId })
        )
      );

      const { success, message, errors } = res.originalData;

      if (!success) {
        snackbar.show({
          severity: 'error',
          message: errors
            ? Object.values(errors).join(', ')
            : message ??
              'Failed to get emailer details. Please try again later or contact support'
        });
        return;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const updateEmailerContent = useCallback(
    async (payload: UpdateEmailerDetailsRequest) => {
      const res = unwrapResult(
        await dispatch(
          actions.updateEmailerContentThunk({
            id: params?.emailerId,
            ...payload
          })
        )
      );

      const { success, message, errors } = res.originalData;

      if (success) {
        snackbar.show({
          severity: 'success',
          message: message ?? 'Content updated successfully.'
        });

        getEmailerDetails();
      } else {
        snackbar.show({
          severity: 'error',
          message: errors
            ? Object.values(errors).join(', ')
            : message ??
              'Failed to update emailer content. Please try again later or contact support'
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const setFilters = useCallback((p) => {
    dispatch(actions.setFilters(p));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initialValues: EmailerDetails = {};

  const formik = useFormik({
    initialValues,

    onSubmit: (values) => {
      if (!formik.dirty) {
        snackbar.show({
          severity: 'error',
          message: 'No Changes Detected'
        });
        return;
      }

      if (params?.isCreateForm) {
        createEmailer(values);
      } else {
        // pass dirty values only
        const dirtyValues = getDirtyValues(values, formik.initialValues);
        updateEmailerDetails(dirtyValues);
      }
    }
  });

  const uploadEmailImageAsset = async (
    params: UploadImageEmailAssetPayload
  ) => {
    try {
      const response = unwrapResult(
        await dispatch(actions.uploadEmailImageAssetThunk(params))
      );

      if (!response?.success) {
        snackbar?.show({
          severity: 'error',
          message:
            response?.errors?.[0] || 'failed to upload image please try again'
        });

        return null;
      }

      const data = response;

      snackbar?.show({
        severity: 'success',
        message: response?.message
      });

      return data;
    } catch (e) {
      console.error(e);

      snackbar?.show({
        severity: 'error',
        message: 'Unexpected server error'
      });

      return null;
    }
  };

  return {
    formik,
    emailerDetails,
    emailerList,
    meta,
    isLoading,
    filters,
    canCreateEmailer,
    canUpdateEmailer,

    uploadEmailImageAsset,
    getEmailerList,
    getEmailerDetails,
    setFilters,
    updateEmailerContent,
    updateEmailerDetails
  };
};
